import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from "wouter";
import _ from "lodash";
import { actions } from '~/store/store.js';
import PromptSetForm from '~/components/PromptSetForm.jsx';
import DateTime from "~/functions/DateTime.js";
import DottedSpinner from "~/components/DottedSpinner.jsx";
import CertificateSVG from '~/components/CertificateSVG.jsx';
import useCurrentAccount from "~/hooks/useCurrentAccount.js";
import { certificationExamResults } from '~/constants.js';
import { useDataEnv } from "~/functions/DataEnv.js";
import { canAccessFeature } from "~/functions/Access.js";
import redirect from "~/functions/redirect.js";

const CertificationExamPod = ({ podule, feature, ...props }) => {
    const dataEnv = useDataEnv();
    const featurePlan = _.get(dataEnv, 'current_account.feature_plan');
    const hasAccess = canAccessFeature(featurePlan, podule);

    useEffect(() => {
        if (!hasAccess) redirect(Routes.path_path('pricing'));
    }, []);

    const dispatch = useDispatch();

    const promptSet = useSelector(({ promptSets }) =>
        Object.values(promptSets.records).find((promptSet) => promptSet.identity === podule.itself)
    );

    const { currentAccount } = useCurrentAccount();

    const trainingProfile = useSelector(({ trainingProfiles }) => trainingProfiles.currentTrainingProfile);

    const certification = useSelector(({ certifications }) => certifications?.records[promptSet?.promptable_id]);

    const examResults = useSelector(({ certificationExamResults }) => Object.values(certificationExamResults.records).filter((examResult) => {
        return examResult.training_profile_id === trainingProfile?.id && examResult.certification_id === promptSet?.promptable_id;
    }));

    useEffect(() => {
        dispatch(actions.promptSets.loadById({ id: podule.itself }));
        dispatch(actions.trainingProfiles.loadCurrentTrainingProfile());
    }, []);

    useEffect(() => {
        if (!promptSet) return;
        dispatch(actions.promptSetSubmissions.query({ prompt_set_id: promptSet.id, responder_type: 'TrainingProfile' }));
        dispatch(actions.certificationExamResults.query({ certification_id: promptSet.promptable_id }));
        dispatch(actions.certifications.loadById(promptSet.promptable_id));
    }, [promptSet]);

    const hasPassingResult = useMemo(() => examResults.some(result => result.pass), [examResults]);
    const passingResult = useMemo(() => examResults.find(result => result.pass));
    const pendingResult = useMemo(() => {
        if (!examResults || examResults.length === 0) return;
        const mostRecent = examResults?.reduce((current, latest) => new Date(current.created_at) > new Date(latest.created_at) ? current : latest);
        return mostRecent?.result === certificationExamResults.pendingResult;
    }, [examResults]);

    const afterSubmit = (event) => {
        dispatch(actions.promptSetSubmissions.query({
            prompt_set_id: promptSet.id, responder_type: 'TrainingProfile', after: DateTime.minutesAgo(1).toISOString()
        }));
    };

    return (
        <>
            <h1>Certification Exam</h1>
            {!!promptSet && !hasPassingResult && (
                <PromptSetForm promptSetId={promptSet.id} prompts={promptSet.prompts} afterSubmit={afterSubmit} responderType="TrainingProfile"  />
            )}

            {!promptSet || (hasPassingResult && (!certification || !currentAccount)) && (
                <DottedSpinner />
            )}

            {certification && hasPassingResult && (
                <>
                    <div className="mb-4 pair-row">
                        <div className="pair-first">
                            <CertificateSVG />
                        </div>
                        <div className="pair-second d-flex w-50 justify-content-center">
                            <div className="d-inline-block">
                                <h3>Certificate of Proficiency</h3>
                                <p>In {certification.name}<br/>
                                Awarded to {currentAccount.name}<br/>
                                <i>{DateTime.mdy(passingResult.created_at)}</i></p>
                            </div>
                        </div>
                    </div>
                    <div className="cert-banner completion-banner">
                        <span className="icon fa fa-medal fa-2x"></span> <span>Complete</span>
                    </div>
                </>
            )}

            {certification && pendingResult && (
                <>
                    <div className="mb-4 pair-row p-5">
                        <div className="pair-first w-50 text-center">
                            <span className="fa fa-hourglass-2 fa-6x"></span>
                        </div>
                        <div className="pair-second d-flex w-50">
                            <div className="d-inline-block">
                                <h3>Result Pending...</h3>
                            </div>
                        </div>
                    </div>
                    <div className="cert-banner pending-banner">
                        <span className="icon fa fa-clock fa-2x"></span> <span>In Review</span>
                    </div>
                </>
            )}

            <div className="site-row m-0">
                <Link to={`/`}>&lt; Back to {feature.navTitle}</Link>
            </div>
        </>
    );
};

export default CertificationExamPod;

// if the result is passed, show a certification and complete banner, back to certification
// if the result is submitted and pending, show a message
// if the result is submitted and not passed show on the certification page
// if the result is not passed, show a certification form
// don't allow a retake if passed